import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { useRouter } from "expo-router";
import type { ReactNode } from "react";
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from "react-native";
import Svg, { Circle, Line, Path, Rect } from "react-native-svg";

import { LogoMark, LogoWordmark } from "../brand/Logo";
import { SafeAreaView } from "react-native-safe-area-context";

const FOREST = COLORS.FOREST.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = COLORS.AMBER.hex;
const SAGE = COLORS.SAGE.hex;

function LeafVeinBackground({ opacity = 0.08 }: { opacity?: number }) {
  const { width, height } = useWindowDimensions();
  const w = Math.max(width, 400);
  const h = Math.max(height, 400);
  return (
    <View pointerEvents="none" className="absolute inset-0" style={{ opacity }}>
      <Svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        style={{ position: "absolute", right: -60, top: -40 }}
        preserveAspectRatio="xMaxYMin slice"
      >
        <Path
          d={`M ${w * 0.55} ${h * 0.95} Q ${w * 0.52} ${h * 0.65} ${w * 0.48} ${h * 0.35} T ${w * 0.42} ${h * 0.08}`}
          stroke={SAGE}
          strokeWidth={2.5}
          strokeLinecap="round"
          fill="none"
        />
        <Path
          d={`M ${w * 0.35} ${h * 0.42} Q ${w * 0.55} ${h * 0.38} ${w * 0.78} ${h * 0.32}`}
          stroke={SAGE}
          strokeWidth={1.8}
          strokeLinecap="round"
          fill="none"
        />
        <Path
          d={`M ${w * 0.35} ${h * 0.42} Q ${w * 0.18} ${h * 0.36} ${w * 0.02} ${h * 0.3}`}
          stroke={SAGE}
          strokeWidth={1.8}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
      <Svg
        width={w * 0.9}
        height={h * 0.85}
        viewBox="0 0 600 600"
        style={{ position: "absolute", left: -50, bottom: -60, transform: [{ rotate: "160deg" }, { scaleX: -1 }] }}
        preserveAspectRatio="xMinYMax meet"
      >
        <Path
          d="M300 580 C290 470 268 360 244 272 C224 198 200 132 182 76"
          stroke={SAGE}
          strokeWidth={2.2}
          strokeLinecap="round"
          fill="none"
        />
        <Path
          d="M244 272 C302 246 370 226 432 200 C484 178 526 152 554 128"
          stroke={SAGE}
          strokeWidth={1.6}
          strokeLinecap="round"
          fill="none"
        />
        <Path
          d="M244 272 C186 246 120 226 60 200 C10 178 -30 152 -56 128"
          stroke={SAGE}
          strokeWidth={1.6}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
    </View>
  );
}

export type AuthCardProps = {
  children: ReactNode;
  /** Widen the card to ~760px and use tighter chrome padding. Used by
   *  the signup form so its multi-row fields can pair into 2-column
   *  layouts without spilling vertically below the fold. */
  wide?: boolean;
  /** Override the "← HOME" action. Defaults to navigating to "/", which is
   *  right for signed-out screens — but the MFA challenge page passes a
   *  sign-out-first handler, because a half-authenticated session would
   *  otherwise get bounced straight back to the MFA page by the gate. */
  onHome?: () => void;
};

export function AuthCard({ children, wide = false, onHome }: AuthCardProps) {
  const router = useRouter();
  const cardShadow =
    Platform.OS === "web"
      ? ({ boxShadow: "0 32px 80px rgba(0,0,0,0.3)" } as const)
      : { elevation: 14 };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: FOREST }} edges={["top", "left", "right"]}>
      <View className="absolute inset-0" style={{ backgroundColor: FOREST }} />
      <View className="absolute inset-0" style={{ backgroundColor: FOREST_DEEP, opacity: 0.55 }} />
      <LeafVeinBackground />

      <KeyboardAvoidingView className="flex-1" behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <Pressable
          onPress={onHome ?? (() => router.replace("/" as Href))}
          accessibilityRole="link"
          accessibilityLabel="Home"
          className="absolute left-5 top-3 z-10 px-1 py-2"
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
        >
          <Text
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ fontFamily: "DMSans_600SemiBold", color: "rgba(237,232,223,0.45)" }}
          >
            ← HOME
          </Text>
        </Pressable>

        <ScrollView
          className="flex-1"
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: "center",
            paddingHorizontal: 20,
            paddingVertical: wide ? 24 : 40,
            paddingTop: wide ? 36 : 52,
          }}
          keyboardShouldPersistTaps="handled"
        >
          <View
            className={`w-full ${wide ? "max-w-[760px]" : "max-w-[520px]"} self-center overflow-hidden rounded-md`}
            style={cardShadow}
          >
            <View
              className={`flex-row items-center justify-center gap-3 px-9 ${wide ? "py-5" : "py-9"}`}
              style={{
                backgroundColor: FOREST_MID,
                borderBottomWidth: 1,
                borderBottomColor: "rgba(181,135,58,0.15)",
              }}
            >
              <LogoMark size={wide ? 24 : 30} tile={FOREST} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
              <LogoWordmark
                className={`${wide ? "text-[16px]" : "text-[19px]"} font-semibold tracking-widest-2 text-linen`}
                accent={AMBER}
              />
            </View>

            <View
              className={`${wide ? "px-9 py-7" : "px-9 py-10"}`}
              style={{ backgroundColor: COLORS.SAND.hex }}
            >
              {children}
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
